import { models } from "types/api/viewModels.ts";
import { findAndReturnBillToInfoFromDraft } from "utils/trucks.ts";
import { FC, Fragment, useMemo } from "react";
import { StyledTableCell } from "components/Common/Table/StyledTable.styles.ts";
import { format } from "date-fns";
import { RemoveText, UnavailableDataPlaceholderText, ViewDraftText } from "constants/text.ts";
import { displayBillToInfo } from "utils/accounts.ts";
import { removeConfigWithDupeProductLine } from "components/NewOrders/SaveDraft/SaveDraftTable/saveDraftUtils.ts";
import { epsilonRounding } from "utils/epsilonRounding.ts";
import { getCabCubeCountsFromDraft } from "utils/order.ts";
import { LinkButton } from "components/Common/Link";
import { checkForAndReplaceNullEmptyWhitespace } from "utils/string";

import { Link } from "@mui/material";
import useEmulationParams from "hooks/useEmulationParams";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { useDialog } from "hooks/useDialog.ts";

interface TruckOrdersTableRowProps {
	draft: models["DraftOrderViewModel"];
	billTos: models["BillToViewModel"][];
	truck: models["TruckViewModel"] | undefined;
}

const TruckOrdersTableRow: FC<TruckOrdersTableRowProps> = ({ draft, billTos, truck }) => {
	const emulationParams = useEmulationParams();
	const { openDialog } = useDialog();

	const billToInfo = useMemo(() => findAndReturnBillToInfoFromDraft(draft, billTos), [billTos, draft]);

	const truckStopIndex = truck?.stops?.findIndex((stop) =>
		stop.draftOrders?.find((draftOrder) => draftOrder.draftOrderId === draft.draftOrderId)
	);

	return (
		<Fragment>
			<StyledTableCell
				data-testid="truck-orders-table-createdDate"
				fixedWidth="107px"
			>
				{draft.createdAt ? format(draft.createdAt, "MM/dd/yy") : UnavailableDataPlaceholderText}
			</StyledTableCell>
			<StyledTableCell
				data-testid="truck-orders-table-billTo"
				minWidth="114px"
			>
				<div>{displayBillToInfo(billToInfo)}</div>
			</StyledTableCell>
			<StyledTableCell
				maxWidth="120px"
				minWidth="80px"
				data-testid="truck-orders-table-poNumber"
			>
				{checkForAndReplaceNullEmptyWhitespace(draft.poNumber)}
			</StyledTableCell>
			<StyledTableCell
				maxWidth="120px"
				minWidth="80px"
				data-testid="truck-orders-table-jobName"
			>
				{checkForAndReplaceNullEmptyWhitespace(draft.jobName)}
			</StyledTableCell>
			<StyledTableCell
				data-testid="truck-orders-table-productLine"
				maxWidth="120px"
				minWidth="80px"
			>
				{draft.configurations?.find((config) => config.globals?.productLine?.description) ? (
					removeConfigWithDupeProductLine(draft).configurations?.map((configuration) => (
						<div key={configuration.globals?.productLine?.id}>
							{configuration.globals?.productLine?.description}
						</div>
					))
				) : (
					<div>{UnavailableDataPlaceholderText}</div>
				)}
			</StyledTableCell>
			<StyledTableCell
				data-testid="truck-orders-table-cabinetCount"
				fixedWidth="46px"
			>
				{epsilonRounding(getCabCubeCountsFromDraft(draft).cabinetCount)}
			</StyledTableCell>
			<StyledTableCell
				data-testid="truck-orders-table-cubeCount"
				fixedWidth="46px"
			>
				{epsilonRounding(getCabCubeCountsFromDraft(draft).cubeCount)}
			</StyledTableCell>
			<StyledTableCell
				fixedWidth="75px"
				isLastIndex
			>
				<Link
					href={`/new-order/build-order/${draft.draftOrderId}${emulationParams}`}
					target="_blank"
					data-testid="truck-orders-table-edit-link"
				>
					{ViewDraftText}
				</Link>

				<LinkButton
					variant="remove"
					data-testid="truck-orders-table-remove-link"
					onClick={() =>
						openDialog({
							dialogStatus: DialogTracking.REMOVE_TRUCK_ORDER,
							dialogIndex: truckStopIndex,
							dialogId: draft.draftOrderId
						})
					}
				>
					{RemoveText}
				</LinkButton>
			</StyledTableCell>
		</Fragment>
	);
};

export default TruckOrdersTableRow;
